/**
 * Formatting and date handling.
 *
 * Everything time-related is anchored to **Europe/Madrid**. The DeCA is a
 * Spanish legal document: `fecha_realizacion` means a wall-clock moment in
 * Spain, the import template writes bare `YYYY-MM-DD HH:MM` interpreted there,
 * and a traffic manager reading the portal from another country must still see
 * the time the driver will experience. Nothing here reads the browser zone.
 *
 * Over JSON the contract requires **RFC 3339 with an explicit offset**
 * (`2026-10-06T12:00:00+02:00`); a value with no offset is rejected rather than
 * guessed. The date and hour controls produce exactly the offsetless form —
 * `YYYY-MM-DD`, and `HH:MM` when an hour is given, joined by `joinServiceDate` —
 * so `localInputToRfc3339` is what closes that gap.
 */

export const DOCUMENT_TIME_ZONE = 'Europe/Madrid'

/**
 * The years a DeCA's dates may name, 2000 to 2099, as the bounds of every date control that writes
 * one. Every such control is `type="date"` since #102 made the route sheet's hour optional; the
 * `datetime-local` pair went with the last control that used it.
 *
 * DECA-BACKEND-A/B/C (production, 2026-09-27): a date control takes a year typed as «26» as the
 * year 0026. The service stored it, could not read it back, and answered 500 on every read that met
 * it, the whole list included; it now refuses the date instead. These put the same range in the
 * browser's own picker, and that is all they do: a year typed outside it is still sent, because the
 * forms submit through `form.handleSubmit` and never through the browser's validation, and it is
 * the service's `OUT_OF_RANGE` that lands on the field.
 */
export const SERVICE_DATE_BOUNDS = { min: '2000-01-01', max: '2099-12-31' } as const

/** es-ES is primary; en falls back to en-GB so dates stay day-first. */
export function intlLocale(language: string): string {
  return language.startsWith('en') ? 'en-GB' : 'es-ES'
}

function offsetMinutesAt(utcMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DOCUMENT_TIME_ZONE,
    timeZoneName: 'longOffset',
  }).formatToParts(new Date(utcMs))
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(raw)
  if (!match) return 0
  const [, sign, hh, mm] = match
  const magnitude = Number(hh) * 60 + Number(mm)
  return sign === '-' ? -magnitude : magnitude
}

function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `${sign}${hh}:${mm}`
}

/**
 * `2026-10-06T12:00` (as typed, meaning Spanish local time) →
 * `2026-10-06T12:00:00+02:00`.
 *
 * The offset is resolved by asking the zone what it was at that instant, then
 * refining once — which is what makes a date on the far side of a DST switch
 * come out right rather than an hour off. In the one genuinely ambiguous hour of
 * an autumn fall-back this resolves to the first (summer-time) reading; there is
 * no representation of the ambiguity in the input control, and the server
 * normalises what it receives.
 */
export function localInputToRfc3339(value: string): string {
  if (!value) return ''
  // A bare `YYYY-MM-DD` is a service date with **no time of day**, optional since 2026-09-18. It
  // becomes that day's midnight in Madrid; `fecha_realizacion_has_time: false` is what says it
  // means a day rather than an instant, and this function does not carry that — see
  // `serviceDateInput`.
  const withTime = value.length === 10 ? `${value}T00:00` : value
  const withSeconds = withTime.length === 16 ? `${withTime}:00` : withTime
  const provisional = Date.parse(`${withSeconds}Z`)
  if (Number.isNaN(provisional)) return ''
  const first = offsetMinutesAt(provisional)
  const refined = offsetMinutesAt(provisional - first * 60_000)
  return `${withSeconds}${formatOffset(refined)}`
}

function madridParts(iso: string): Record<string, string> {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return {}
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DOCUMENT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)
  return Object.fromEntries(parts.map((p) => [p.type, p.value]))
}

/**
 * The calendar month containing `now`, shifted by `offsetMonths`, as the pair of
 * bounds `/decas` and `/decas/stats` both accept.
 *
 * The month is a *Spanish* calendar month, not the host's: a manager in another
 * zone must see the same "este mes" as the documents they are counting. The
 * year/month pair is shifted arithmetically rather than through `Date`, so a
 * step across December cannot be re-read in the local zone on the way back.
 */
export function monthWindow(
  offsetMonths = 0,
  now: Date = new Date(),
): { fechaDesde: string; fechaHasta: string } {
  const parts = madridParts(now.toISOString())
  const index = Number(parts.year) * 12 + (Number(parts.month) - 1) + offsetMonths
  const year = Math.floor(index / 12)
  const month = (index % 12) + 1
  // Day 0 of the following month is the last day of this one.
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const pad = (value: number) => String(value).padStart(2, '0')

  return {
    fechaDesde: localInputToRfc3339(`${year}-${pad(month)}-01T00:00:00`),
    fechaHasta: localInputToRfc3339(`${year}-${pad(month)}-${pad(lastDay)}T23:59:59`),
  }
}

/**
 * Month-over-month change as a percentage, or `null` when there is no honest
 * one to state. A previous month of zero has no percentage — every increase
 * from it is "infinite" — so the Panel drops the chip rather than printing a
 * figure it cannot defend.
 */
export function percentChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null
  if (previous <= 0) return null
  return Math.round(((current - previous) / previous) * 100)
}

/** RFC 3339 → the `datetime-local` value that shows Spanish wall-clock time. */
export function rfc3339ToLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const p = madridParts(iso)
  if (!p.year) return ''
  // `hour: '2-digit'` with hour12:false can yield "24" at midnight on some ICU
  // builds; normalise so the control does not reject the value.
  const hour = p.hour === '24' ? '00' : p.hour
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`
}

/**
 * The service date as the portal shows it: with a time only when one was given.
 *
 * 🚨 Reads the flag, never the hour. A date-only document is stored as that day's 00:00 in Madrid
 * and 00:00 is a real departure time, so `hasTime` is the only thing that can tell them apart.
 * `undefined` means `true`: a document written before 2026-09-18 carried a time, and so does a
 * cached list row from before the field existed.
 */
export function formatServiceDate(
  iso: string | null | undefined,
  hasTime: boolean | undefined,
  language: string,
): string {
  if (hasTime ?? true) return formatDateTime(iso, language)
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  // `dateStyle: 'short'`, not `formatDate`'s `'medium'`: these two sit in the same list column and
  // the same detail field as timed documents, and «6 ago 2026» beside «6/8/26, 8:30» reads as two
  // different kinds of date rather than one with an hour and one without.
  return new Intl.DateTimeFormat(intlLocale(language), {
    timeZone: DOCUMENT_TIME_ZONE,
    dateStyle: 'short',
  }).format(date)
}

/** An RFC 3339 instant with its offset: how the server records a date field's old and new values. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/

/**
 * A history entry's old or new value as the portal shows it: a stored instant as a date, anything
 * else exactly as recorded. Until 2026-10-09 the instants were printed raw — «2026-10-06T22:00:00.000Z
 * · No longer valid» — while the PDF and the description column read «07-10-2026».
 *
 * [hasTime] is the entry's own word on whether that side carried an hour (`oldHasTime`,
 * `newHasTime`, #135), and then the value is shown as the PDF printed it: a change that only takes
 * the hour off a midnight has the same instant on both sides, and read «7/10/26 → 7/10/26» without
 * it. An entry recorded before dates could be given without an hour says nothing, and an instant at
 * exactly midnight in the documents' time zone is shown as the day alone, which is how a date-only
 * date is stored: a timed service at exactly 00:00 then reads as its day.
 */
export function formatHistoryValue(
  value: string | null | undefined,
  language: string,
  hasTime?: boolean | null,
): string | null {
  if (value === null || value === undefined) return null
  if (!INSTANT.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  if (typeof hasTime === 'boolean') return formatServiceDate(value, hasTime, language)
  const clock = new Intl.DateTimeFormat('en-GB', {
    timeZone: DOCUMENT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
  return formatServiceDate(value, clock !== '00:00', language)
}

/**
 * Splits an RFC 3339 instant into the two controls the form draws: a date, and a time that may be
 * empty. `hasTime: false` yields an empty time box rather than `00:00`, so a user amending a
 * date-only document does not have to notice and clear a midnight they never typed.
 */
export function splitServiceDate(
  iso: string | null | undefined,
  hasTime: boolean | undefined,
): { date: string; time: string } {
  const local = rfc3339ToLocalInput(iso)
  if (!local) return { date: '', time: '' }
  return { date: local.slice(0, 10), time: (hasTime ?? true) ? local.slice(11, 16) : '' }
}

/** The inverse: the two controls back into the value `localInputToRfc3339` takes. */
export function joinServiceDate(date: string, time: string): string {
  if (!date) return ''
  return time ? `${date}T${time}` : date
}

/**
 * A service date as the two controls carry it, `YYYY-MM-DD[THH:MM]` in Madrid, which is also what the
 * PDF prints of it: the day, and the minute only when there is an hour. Two dates print the same
 * exactly when these are equal, and that is how the server's diff judges a date (#135).
 */
export function serviceDateValue(iso: string | null | undefined, hasTime: boolean | undefined): string {
  const { date, time } = splitServiceDate(iso, hasTime)
  return joinServiceDate(date, time)
}

export function formatDateTime(iso: string | null | undefined, language: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(intlLocale(language), {
    timeZone: DOCUMENT_TIME_ZONE,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

export function formatDate(iso: string | null | undefined, language: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(intlLocale(language), {
    timeZone: DOCUMENT_TIME_ZONE,
    dateStyle: 'medium',
  }).format(date)
}

export function formatNumber(value: number | null | undefined, language: string): string {
  if (value == null) return ''
  return new Intl.NumberFormat(intlLocale(language), { maximumFractionDigits: 3 }).format(value)
}

/** `20000` + `kg` → `20.000 kg`. The unit is never merged into the number. */
export function formatQuantity(
  value: number | null | undefined,
  unit: string | null | undefined,
  language: string,
): string {
  if (value == null) return ''
  return unit ? `${formatNumber(value, language)} ${unit}` : formatNumber(value, language)
}

/** `now` is injectable so the relative label is testable without waiting. */
export function formatRelativeDays(
  iso: string | null | undefined,
  language: string,
  now: number = Date.now(),
): string {
  if (!iso) return ''
  const ms = Date.parse(iso) - now
  if (Number.isNaN(ms)) return ''
  const days = Math.round(ms / 86_400_000)
  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), { numeric: 'auto' })
  if (Math.abs(days) < 1) {
    return rtf.format(Math.round(ms / 3_600_000), 'hour')
  }
  return rtf.format(days, 'day')
}

// `normaliseIdentifier` used to live here, labelled "never for comparison" and imported by
// nothing. It now lives in `deca/identifiers.ts`, which is where the comparison rule belongs and
// where the pairing with `backend/src/deca/identifiers.ts` is stated.

/** The CSV is printed in three groups: `X4T9-KM2P-7RQ8`. */
export function formatCsv(csv: string | null | undefined): string {
  if (!csv) return ''
  const clean = csv.replace(/-/g, '').toUpperCase()
  if (clean.length !== 12) return csv
  return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 12)}`
}
